import { useState } from "react";
import {
  Heart,
  Activity,
  Watch,
  Smartphone,
  Shield,
  MessageCircle,
  ChevronDown,
  Menu,
  X,
  MapPin,
  BookOpen,
  Award,
} from "lucide-react";

// ─── Navbar ──────────────────────────────────────────────────────────────────
function Navbar({ onLoginClick }) {
  const [open, setOpen] = useState(false);
  const links = ["Beranda", "Fitur", "Tentang", "Kontak"];

  return (
    <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur border-b border-gray-100 shadow-sm">
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shadow">
            <img
              src="/logo_splash2.png"
              alt="SmartMoms Logo"
              className="w-7 h-7 object-contain"
            />
          </div>
          <span className="font-display font-800 text-xl text-primary-800">
            Smart<span className="text-accent-700">Moms</span>
          </span>
        </div>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l}
              href={`#${l.toLowerCase()}`}
              className="text-sm font-medium text-gray-600 hover:text-primary-800 transition-colors"
            >
              {l}
            </a>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={onLoginClick}
            className="px-5 py-2 rounded-lg bg-primary-800 text-white text-sm font-semibold hover:bg-primary-900 transition-colors shadow-sm"
          >
            Login Admin
          </button>
        </div>

        {/* Mobile Toggle */}
        <button className="md:hidden" onClick={() => setOpen(!open)}>
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-3">
          {links.map((l) => (
            <a
              key={l}
              href={`#${l.toLowerCase()}`}
              className="text-sm font-medium text-gray-700 hover:text-primary-800"
              onClick={() => setOpen(false)}
            >
              {l}
            </a>
          ))}
          <button
            onClick={onLoginClick}
            className="mt-2 w-full py-2 rounded-lg bg-primary-800 text-white text-sm font-semibold"
          >
            Login Admin
          </button>
        </div>
      )}
    </nav>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function Hero({ onLoginClick }) {
  return (
    <section
      id="beranda"
      className="min-h-screen pt-16 bg-gradient-to-br from-primary-800 via-primary-700 to-primary-900 flex items-center relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-white blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 rounded-full bg-accent-500 blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Text */}
        <div>
          <div className="inline-flex items-center gap-2 bg-white/15 text-white/90 text-xs font-semibold px-4 py-2 rounded-full mb-6 border border-white/20">
            <Activity className="w-3.5 h-3.5" />
            Monitoring Kesehatan Ibu Nifas
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-900 text-white leading-tight mb-4">
            Pantau Kesehatan Ibu Nifas dengan
            <span className="text-accent-400"> Cerdas & Real-time</span>
          </h1>
          <p className="text-white/80 text-base leading-relaxed mb-8">
            SmartMoms menggabungkan teknologi smartwatch dan aplikasi Android
            untuk memantau kondisi ibu pasca melahirkan secara real-time — detak
            jantung, tekanan darah, aktivitas fisik, dan deteksi dini depresi
            pascapersalinan.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={onLoginClick}
              className="px-7 py-3 bg-accent-700 hover:bg-accent-800 text-white font-semibold rounded-xl shadow-lg transition-all hover:-translate-y-0.5"
            >
              Masuk ke Dashboard
            </button>
            <a
              href="#fitur"
              className="px-7 py-3 bg-white/15 hover:bg-white/25 text-white font-semibold rounded-xl border border-white/30 transition-all flex items-center gap-2"
            >
              Pelajari Fitur <ChevronDown className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Visual mockup */}
        <div className="hidden md:flex justify-center">
          <div className="relative">
            {/* Phone mockup */}
            <div className="w-56 h-96 bg-white rounded-3xl shadow-2xl border-4 border-white/80 overflow-hidden">
              <div className="bg-primary-800 h-12 flex items-center justify-center">
                <span className="text-white text-xs font-bold font-display">
                  SmartMoms
                </span>
              </div>
              <div className="p-4 space-y-3">
                <StatCard label="BPM" value="82" color="text-red-500" />
                <StatCard
                  label="Tekanan Darah"
                  value="120/80"
                  color="text-primary-700"
                />
                <StatCard
                  label="Langkah Hari Ini"
                  value="4.320"
                  color="text-green-600"
                />
                <div className="bg-accent-50 rounded-xl p-3 border border-accent-100">
                  <p className="text-xs font-semibold text-accent-700 mb-1">
                    Skor EPDS
                  </p>
                  <div className="flex items-end gap-1">
                    <span className="text-2xl font-bold font-display text-accent-700">
                      8
                    </span>
                    <span className="text-xs text-gray-400 mb-1">/30</span>
                  </div>
                  <p className="text-xs text-green-600 font-medium">Normal</p>
                </div>
              </div>
            </div>
            {/* Watch mockup */}
            <div className="absolute -right-12 top-16 w-24 h-28 bg-gray-900 rounded-2xl shadow-xl border-2 border-gray-700 flex flex-col items-center justify-center gap-1">
              <Watch className="w-5 h-5 text-accent-400" />
              <span className="text-white text-xs font-bold">82</span>
              <span className="text-gray-400 text-xs">BPM</span>
              <div className="w-12 h-1 bg-green-400 rounded-full mt-1" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-between">
      <span className="text-xs text-gray-500">{label}</span>
      <span className={`text-sm font-bold font-display ${color}`}>{value}</span>
    </div>
  );
}

// ─── Features ─────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: Activity,
    title: "Monitoring BPM Real-time",
    desc: "Pantau detak jantung ibu secara langsung via smartwatch yang terhubung ke aplikasi.",
    color: "bg-red-50 text-red-600",
  },
  {
    icon: Shield,
    title: "Deteksi Tekanan Darah",
    desc: "Data tekanan darah disinkronkan otomatis dan dikirim ke dokter pendamping.",
    color: "bg-primary-50 text-primary-700",
  },
  {
    icon: Watch,
    title: "Koneksi Smartwatch",
    desc: "Integrasi langsung dengan smartwatch untuk monitoring tanpa gangguan.",
    color: "bg-purple-50 text-purple-600",
  },
  {
    icon: BookOpen,
    title: "Kuesioner EPDS",
    desc: "Deteksi dini depresi pascapersalinan dengan kuesioner Edinburgh Postnatal Depression Scale.",
    color: "bg-accent-50 text-accent-700",
  },
  {
    icon: MessageCircle,
    title: "Pesan dari Dokter",
    desc: "Dokter dapat mengirim pesan edukasi dan saran langsung ke aplikasi ibu.",
    color: "bg-green-50 text-green-600",
  },
  {
    icon: Smartphone,
    title: "Aplikasi Android",
    desc: "Antarmuka yang ramah pengguna, mudah dipakai oleh ibu nifas tanpa keahlian teknis.",
    color: "bg-sky-50 text-sky-600",
  },
];

function Features() {
  return (
    <section id="fitur" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-bold text-primary-700 uppercase tracking-widest mb-3">
            Fitur Unggulan
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-800 text-gray-900 mb-4">
            Teknologi untuk Ibu yang Lebih Sehat
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto">
            Ekosistem lengkap yang menghubungkan ibu nifas, smartwatch, dan
            tenaga kesehatan dalam satu platform terintegrasi.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="p-6 rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-md transition-all group"
            >
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${f.color}`}
              >
                <f.icon className="w-6 h-6" />
              </div>
              <h3 className="font-display font-700 text-gray-900 mb-2">
                {f.title}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── About ────────────────────────────────────────────────────────────────────
function About() {
  return (
    <section
      id="tentang"
      className="py-24 bg-gradient-to-br from-gray-50 to-primary-50"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-bold text-primary-700 uppercase tracking-widest mb-3">
            Tentang Kami
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-800 text-gray-900 mb-4">
            Didukung oleh Institusi Terpercaya
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Abdurrab */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="w-14 h-14 rounded-2xl bg-primary-800 flex items-center justify-center mb-5 shadow">
              <Award className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-display font-800 text-xl text-gray-900 mb-2">
              Universitas Abdurrab
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-4">
              SmartMoms merupakan proyek penelitian dari Universitas Abdurrab,
              Pekanbaru, Riau. Dikembangkan oleh tim dosen dan mahasiswa yang
              berkomitmen pada inovasi kesehatan digital untuk masyarakat
              Indonesia.
            </p>
            <div className="flex items-center gap-2 text-primary-700 text-sm font-medium">
              <MapPin className="w-4 h-4" />
              Pekanbaru, Riau, Indonesia
            </div>
          </div>

          {/* Diktisaintek */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="w-14 h-14 rounded-2xl bg-accent-700 flex items-center justify-center mb-5 shadow">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-display font-800 text-xl text-gray-900 mb-2">
              Diktisaintek Berdampak
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-4">
              Proyek ini didukung melalui program{" "}
              <strong>Diktisaintek Berdampak</strong> dari Kementerian
              Pendidikan Tinggi, Sains, dan Teknologi Republik Indonesia, yang
              mendorong riset perguruan tinggi yang memberikan dampak nyata bagi
              masyarakat.
            </p>
            <div className="flex items-center gap-2 text-accent-700 text-sm font-medium">
              <Award className="w-4 h-4" />
              Program Hibah Riset Nasional
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-10 grid grid-cols-3 gap-6">
          {[
            { value: "100+", label: "Ibu Nifas Terdaftar" },
            { value: "24/7", label: "Monitoring Real-time" },
            { value: "98%", label: "Akurasi Deteksi EPDS" },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-white rounded-2xl p-6 text-center shadow-sm border border-gray-100"
            >
              <div className="font-display text-3xl font-900 text-primary-800 mb-1">
                {s.value}
              </div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer({ onLoginClick }) {
  return (
    <footer id="kontak" className="bg-primary-900 text-white py-12">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-8 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <Heart className="w-4 h-4 text-white fill-white" />
              </div>
              <span className="font-display font-800 text-lg">
                Smart<span className="text-accent-400">Moms</span>
              </span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed">
              Platform monitoring kesehatan ibu nifas berbasis IoT dan Android.
            </p>
          </div>

          <div>
            <h4 className="font-display font-700 mb-4 text-white/90">
              Navigasi
            </h4>
            <ul className="space-y-2">
              {["Beranda", "Fitur", "Tentang", "Kontak"].map((l) => (
                <li key={l}>
                  <a
                    href={`#${l.toLowerCase()}`}
                    className="text-white/60 hover:text-white text-sm transition-colors"
                  >
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-700 mb-4 text-white/90">
              Untuk Tenaga Kesehatan
            </h4>
            <p className="text-white/60 text-sm mb-4">
              Login ke panel admin untuk memantau pasien dan mengelola data
              kesehatan.
            </p>
            <button
              onClick={onLoginClick}
              className="px-5 py-2.5 bg-accent-700 hover:bg-accent-800 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Login Admin →
            </button>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-2">
          <p className="text-white/40 text-xs">
            © 2024 SmartMoms — Universitas Abdurrab. Didukung Diktisaintek
            Berdampak.
          </p>
          <p className="text-white/40 text-xs">Pekanbaru, Riau, Indonesia</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────
export default function LandingPage({ onLoginClick }) {
  return (
    <div className="min-h-screen">
      <Navbar onLoginClick={onLoginClick} />
      <Hero onLoginClick={onLoginClick} />
      <Features />
      <About />
      <Footer onLoginClick={onLoginClick} />
    </div>
  );
}
